// components/AvatarGroupCreationModal.tsx
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/system";
import { THEME_COLORS } from "../../../../context/constants";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: "10px",
    minWidth: "400px",
  },
  "& .MuiBackdrop-root": {
    position: "relative",
  },
}));

interface AvatarGroupCreationModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (avatarId: string, groupName: string) => Promise<void>;
  avatarName: string;
  avatarId: string;
  isLoading: boolean;
}

const AvatarGroupCreationModal: React.FC<AvatarGroupCreationModalProps> = ({
  open,
  onClose,
  onSubmit,
  avatarName,
  avatarId,
  isLoading,
}) => {
  const [groupName, setGroupName] = useState(`${avatarName} Group`);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Add this
    await onSubmit(avatarId, groupName);
    // Don't reset the name here - wait for success
  };

  const handleClose = () => {
    onClose();
    setGroupName(`${avatarName} Group`); // Reset on close
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Create Avatar Group</Typography>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box>
            <Typography
              variant="body2"
              color="black"
              sx={{ mb: 4, fontSize: "12px" }}
            >
              First, we'll create an avatar group based on your chosen actor to
              maintain consistency. Then you can add multiple looks with
              different styles and appearances.
            </Typography>
            <TextField
              fullWidth
              label="Group Name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              required
              disabled={isLoading}
              sx={{
                "& .MuiInputLabel-root": {
                  color: THEME_COLORS.black,
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: THEME_COLORS.yellow,
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: "20px" }}>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit" // This is key - it will trigger the form submission
            variant="contained"
            disabled={!groupName.trim() || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? "Creating..." : "Create Group"}
          </Button>
        </DialogActions>
      </form>
    </StyledDialog>
  );
};

export default AvatarGroupCreationModal;
